/**
 * Zod schema for `GET /api/admin/customer-health`
 * (`apps/workers/src/api/admin/customerHealth.ts`) — the per-customer
 * health dashboard (Stripe billing × platform activity: jobs -> sourced
 * -> sent -> replies). GitHub issue "[Admin] Per-customer health
 * dashboard — Stripe billing × platform activity
 * (jobs->sourced->sent->replies)".
 *
 * Mirrors `AdminCustomerHealthRow` / `AdminCustomerHealthResponse`
 * (`@herohire/types`, `packages/types/src/admin.ts`) field-for-field —
 * see `@/lib/validation/subscriptions.ts`'s header comment for why this
 * workspace duplicates rather than cross-imports the backend's raw
 * TypeScript interfaces (Zod schemas are this app's runtime-validated
 * source of truth for API responses; `@herohire/types` interfaces are
 * the backend's own compile-time contract).
 */

import { z } from 'zod';

export const customerHealthFlagSchema = z.enum(['green', 'yellow', 'red']);
export type AdminCustomerHealthFlag = z.infer<typeof customerHealthFlagSchema>;

export const adminCustomerHealthRowSchema = z.object({
  email: z.string(),
  linkedDbUser: z.boolean(),
  stripeSubscriptionIdPresent: z.boolean(),
  stripeSubscriptionId: z.string(),
  planName: z.string(),
  amountCents: z.number().int(),
  currency: z.string(),
  interval: z.string(),
  jobsOpen: z.number().int().nonnegative(),
  jobsArchived: z.number().int().nonnegative(),
  candidatesSourced: z.number().int().nonnegative(),
  reachablePercent: z.number().int().min(0).max(100),
  outreachSent30d: z.number().int().nonnegative(),
  replies: z.number().int().nonnegative(),
  lastActivity: z.string().nullable(),
  healthFlag: customerHealthFlagSchema,
});
export type AdminCustomerHealthRow = z.infer<typeof adminCustomerHealthRowSchema>;

export const adminCustomerHealthResponseSchema = z.object({
  data: z.array(adminCustomerHealthRowSchema),
  total_count: z.number().int().nonnegative(),
  cached: z.boolean(),
  generated_at: z.string(),
});
export type AdminCustomerHealthResponse = z.infer<typeof adminCustomerHealthResponseSchema>;

/** Column this table can be sorted by, client-side (small dataset — ~20
 * paying customers at this issue's scale — so sorting is done in the
 * component, not pushed to the backend, matching this issue's "First
 * deliverable: a one-time generated table" scope: keep the backend
 * simple, do sort/filter client-side like every other small admin list
 * in this app that doesn't need server pagination). */
export const CUSTOMER_HEALTH_SORT_FIELDS = [
  'email',
  'planName',
  'amountCents',
  'jobsOpen',
  'candidatesSourced',
  'outreachSent30d',
  'replies',
  'healthFlag',
] as const;
export type CustomerHealthSortField = (typeof CUSTOMER_HEALTH_SORT_FIELDS)[number];
